/**
 * Entry application component used to compose providers and render Routes.
 * */

// DEPENDENCIES
import React from "react";
import { Provider, useSelector } from "react-redux";
import AiChatPanel from "./components/AI/AiChatPanel";
import { AiAccessProvider, useAiAccess } from "./AiAccessContext";
import { BrowserRouter } from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";
import { Routes } from "../app/Routes";
import { I18nProvider } from "../_metronic/i18n";
import { LayoutSplashScreen, MaterialThemeProvider } from "../_metronic/layout";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
// STYLES
import '../assets/sass/main.scss';
import '../assets/custom/custom.css';

function AuthenticatedAiPanel() {
  const authToken = useSelector(({ auth }) => auth.authToken);
  const userId = useSelector(({ auth }) => auth.user?.id);
  const { hasAiAccess } = useAiAccess();
  return authToken && userId && hasAiAccess ? <AiChatPanel key={userId} userId={userId} /> : null;
}

export default function App({ store, persistor, basename }) {
  return (
    /* Provide Redux store */
    <Provider store={store}>
      {/* Asynchronously persist redux stores and show `SplashScreen` while it's loading. */}
      <PersistGate persistor={persistor} loading={<LayoutSplashScreen />}>
        {/* Add high level `Suspense` in case if was not handled inside the React tree. */}
        <React.Suspense fallback={<LayoutSplashScreen />}>
          {/* Override `basename` (e.g: `homepage` in `package.json`) */}
          <BrowserRouter basename={basename}>
            {/*This library only returns the location that has been active before the recent location change in the current window lifetime.*/}
            <MaterialThemeProvider>
              {/* Provide `react-intl` context synchronized with Redux state.  */}
              <I18nProvider>
                {/* Render routes with provided `Layout`. */}
                <MuiPickersUtilsProvider utils={MomentUtils}>
                  <AiAccessProvider>
                    <Routes />
                    <AuthenticatedAiPanel />
                  </AiAccessProvider>
                </MuiPickersUtilsProvider>
              </I18nProvider>
            </MaterialThemeProvider>
          </BrowserRouter>
        </React.Suspense>
      </PersistGate>
    </Provider>
  );
}
